/**
 * Encrypted Events External Hook
 *
 * React hook for sending analytics events to the external events API.
 * When VITE_EVENTS_EXTERNAL_ENCRYPTION_ENABLED=true, the payload is encrypted
 * client-side using hybrid AES-256-GCM + RSA-OAEP before transmission.
 * When disabled, the plaintext payload is sent directly.
 *
 * The hook automatically appends `_access` to every event name and merges in:
 *   - eventName            — snake_case event name with _access suffix
 *   - eventNameCanonical   — PascalCase version of the event name
 *   - webRegUserId         — unique user ID from localStorage
 *   - sourceApp            — always "access_webreg"
 *   - dateCreated          — ISO 8601 timestamp
 *   - promocode            — promo code, if the user entered one (optional)
 *   - UTM params           — flattened from URL (e.g. utmSource, utmMedium …)
 *
 * All property values are coerced to string | string[] before sending.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * EVENT CATALOGUE — payloads as they arrive at POST /ingest
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Every event shares the following base structure:
 *
 * ```json
 * {
 *   "encrypt": true,
 *   "event": {
 *     "eventName":          "<event-name>_access",
 *     "eventNameCanonical": "<EventName>Access",
 *     "dateCreated":        "2026-02-19T10:30:00.000Z",
 *     "webRegUserId":       "abc123",
 *     "sourceApp":          "access_webreg",
 *     "promocode":          "QAZTGB",          // omitted when no promo code
 *     "utmSource":          "google",           // omitted when no UTM params
 *     "utmMedium":          "cpc",
 *     // … other utm_* keys
 *     // … event-specific fields below
 *   }
 * }
 * ```
 *
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * ## button_pressed_medicare_continue_access
 * Fired when the user submits the Medicare Coverage form (all fields valid).
 *
 * ```json
 * {
 *   "eventName":    "button_pressed_medicare_continue_access",
 *   "eventNameCanonical": "ButtonPressedMedicareContinueAccess",
 *   "firstName":    "John",
 *   "lastName":     "Doe",
 *   "dateOfBirth":  "01/01/1950",
 *   "zipCode":      "12345",
 *   "mbi":          "1EG4-TE5-MK73"
 * }
 * ```
 *
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * ## button_pressed_eckm_continue_access
 * Fired when the user clicks Continue on the eCKM Health Conditions screen.
 *
 * ```json
 * {
 *   "eventName":            "button_pressed_eckm_continue_access",
 *   "eventNameCanonical":   "ButtonPressedEckmContinueAccess",
 *   "selectedConditions":   ["highBloodPressure", "heartFailure"],  // [] if none selected
 *   "completed":            "true"
 * }
 * ```
 *
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * ## button_pressed_ckm_continue_access
 * Fired when the user clicks Continue on the CKM Health Conditions screen.
 *
 * ```json
 * {
 *   "eventName":            "button_pressed_ckm_continue_access",
 *   "eventNameCanonical":   "ButtonPressedCkmContinueAccess",
 *   "selectedConditions":   ["diabetes"],   // [] if none selected
 *   "completed":            "true"
 * }
 * ```
 *
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * ## button_pressed_behavioral_health_continue_access
 * Fired when the user clicks Continue on the Behavioral Health screen.
 *
 * ```json
 * {
 *   "eventName":            "button_pressed_behavioral_health_continue_access",
 *   "eventNameCanonical":   "ButtonPressedBehavioralHealthContinueAccess",
 *   "selectedConditions":   ["depression", "anxiety"],   // [] if none selected
 *   "completed":            "true"
 * }
 * ```
 *
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * ## button_pressed_track_offering_continue_access
 * Fired when the user selects tracks and clicks Continue on the Track Offering screen.
 *
 * ```json
 * {
 *   "eventName":          "button_pressed_track_offering_continue_access",
 *   "eventNameCanonical": "ButtonPressedTrackOfferingContinueAccess",
 *   "selectedTracks":     ["eCKM", "BH"]   // one or more of: "eCKM", "CKM", "BH"
 * }
 * ```
 *
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * ## button_pressed_consent_continue_access
 * Fired when the user checks the consent checkbox and clicks Continue.
 *
 * ```json
 * {
 *   "eventName":          "button_pressed_consent_continue_access",
 *   "eventNameCanonical": "ButtonPressedConsentContinueAccess",
 *   "consentAgreed":      "true"
 * }
 * ```
 *
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * ## button_pressed_consent_declined_access
 * Fired when the user clicks "I'm not interested" on the Consent Review screen.
 *
 * ```json
 * {
 *   "eventName":          "button_pressed_consent_declined_access",
 *   "eventNameCanonical": "ButtonPressedConsentDeclinedAccess",
 *   "consentAgreed":      "false"
 * }
 * ```
 */

import { useCallback } from 'react';
import { useRegistration } from '@/context/RegistrationContext';
import { encryptedEventsExternalClient } from '@/services/encryptedEventsExternal';
import { EncryptedEventPayload } from '@/types/encryptedEvents';
import { snakeToPascalCase } from '@/utils/encryptedEventsUtils';

/**
 * Arguments for tracking an encrypted event
 */
export type TrackEncryptedEventArguments = {
  /** Event name (e.g., "screen_view_medicare_coverage") */
  event: string;

  /** Page-specific event properties (form fields, user selections, etc.) */
  properties?: Record<string, unknown>;
};

/**
 * Function signature for tracking encrypted events
 */
export type TrackEncryptedEventFunction = (args: TrackEncryptedEventArguments) => void;

/**
 * Hook for tracking encrypted external events
 *
 * Provides a function to track events with automatic inclusion of:
 * - webRegUserId
 * - utmParams
 * - promocode
 * - dateCreated (timestamp)
 *
 * @returns Object containing trackEncryptedEvent function
 */
export const useEncryptedEventsExternal = () => {
  const { webregUserId, utmParams, registrationData } = useRegistration();

  /**
   * Track an external event
   *
   * Builds the event payload with required base fields and page-specific
   * properties, then sends to the external events API. When encryption is
   * enabled the payload is encrypted client-side before transmission.
   *
   * IMPORTANT: All property values are coerced to string | string[].
   *
   * @param event - Event name without the _access suffix (e.g., "button_pressed_medicare_continue")
   * @param properties - Page-specific data in camelCase keys (optional)
   */
  const trackEncryptedEvent: TrackEncryptedEventFunction = useCallback(
    ({ event, properties = {} }) => {
      // Convert all property values to strings or arrays of strings
      const stringifiedProperties: Record<string, string | string[]> = {};
      Object.entries(properties).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          // If it's an array, ensure all elements are strings
          if (Array.isArray(value)) {
            stringifiedProperties[key] = value.map((item) => String(item));
          } else {
            // Otherwise, convert to string
            stringifiedProperties[key] = String(value);
          }
        }
      });

      // Flatten UTM params into individual string properties
      const flattenedUtmParams: Record<string, string> = {};
      if (utmParams) {
        Object.entries(utmParams).forEach(([key, value]) => {
          if (value !== null && value !== undefined) {
            flattenedUtmParams[key] = String(value);
          }
        });
      }

      const timestamp = Date.now();
      const timestampAsISO = new Date(timestamp).toISOString();
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      // Build event payload - ALL VALUES ARE STRINGS OR ARRAYS OF STRINGS
      const eventNameWithSuffix = `${event}_access`;
      const payload: EncryptedEventPayload = {
        encrypt: true,
        event: {
          eventName: eventNameWithSuffix, // snake_case event name with _access suffix
          eventNameCanonical: snakeToPascalCase(eventNameWithSuffix), // PascalCase version
          happened_at_device_timezone: timezone,
          happend_at_moment: timestamp,
          happened_at_moment_datetime: timestampAsISO,
          webRegUserId: webregUserId,
          sourceApp: 'access_webreg', // Identifies the source application
          ...(registrationData.promoCodeData?.code && {
            promocode: registrationData.promoCodeData.code,
          }),
          ...flattenedUtmParams, // Flattened UTM params (utmSource, utmMedium, etc.)
          ...stringifiedProperties, // Page-specific data (all values converted to strings or arrays of strings)
        },
      };

      console.log('[useEncryptedEventsExternal] Tracking event:', payload);

      // Send event (async, fire-and-forget)
      encryptedEventsExternalClient.sendEvent(payload).catch((error) => {
        // Errors are already tracked to Mixpanel by the service
        // Just log to console for debugging
        console.error('[useEncryptedEventsExternal] Error sending event:', error);
      });
    },
    [webregUserId, utmParams, registrationData],
  );

  return {
    trackEncryptedEvent,
  };
};
